// ============================================================
// SupprimerDevis — la seule action de cette application qui détruit.
// ------------------------------------------------------------
// La route existait depuis le premier jour (`DELETE /devis/:id`, rôle
// admin, journalisée avant l'effacement) et **aucun écran ne l'appelait**.
// Un devis d'essai ne pouvait donc se retirer qu'en `psql`, par tunnel
// SSH — c'est-à-dire par moi.
//
// ── Pourquoi une confirmation par la RÉFÉRENCE et pas un `confirm()` ──
//
// Ailleurs dans cet écran, `window.confirm` suffit : supprimer un
// document déposé se répare en le redéposant. Ici, non. La suppression
// emporte en cascade les pièces, la GAMME, les paliers, la
// sous-traitance, les documents générés — et `deleteDevis` efface
// ensuite du DISQUE les plans du client, qui ne sont nulle part
// ailleurs. Le dernier instantané `restic` de la nuit est le seul
// recours, et il ne se restaure pas en un clic.
//
// Une boîte « OK / Annuler » se valide au réflexe. Recopier
// « 2026-08-025 » demande de LIRE la référence — donc de vérifier qu'on
// est sur le bon devis. C'est le seul garde-fou qui coûte le geste juste
// et empêche le geste distrait.
//
// ── Ce qui est montré AVANT de détruire ──
//
// « Avant de supprimer ou d'écraser, regarde la cible » : le nombre de
// pièces et l'état du devis sont affichés dans la boîte. Un devis
// ENVOYÉ porte un avertissement de plus — c'est une pièce commerciale,
// pas un brouillon. On ne l'interdit pas pour autant : un devis envoyé
// par erreur doit pouvoir disparaître, et l'interdire reviendrait à
// dire « passez par la base », c'est-à-dire par personne.
// ============================================================

import { useState } from 'react';
import Modal from '../../ui/Modal.jsx';
import Button from '../../ui/Button.jsx';
import { TextField } from '../../ui/TextField.jsx';
import { api } from '../../lib/api.js';

/**
 * @param {object} p
 * @param {boolean} [p.sansBouton]  n'affiche QUE la boîte de
 *   confirmation, pilotée de l'extérieur. La liste des devis a ses
 *   propres icônes de ligne ; ce qui ne doit pas exister en double, ce
 *   n'est pas le bouton — c'est la CONFIRMATION. Deux boîtes de
 *   confirmation finiraient par diverger, et c'est la plus laxiste qui
 *   servirait.
 */
export function SupprimerDevis({
  devis, pieces = 0, onSupprime, sansBouton = false, ouvertExterne = false, onFermer = null,
}) {
  const [ouvertLocal, setOuvertLocal] = useState(false);
  const ouvert = sansBouton ? ouvertExterne : ouvertLocal;
  const setOuvert = (v) => (sansBouton ? (!v && onFermer?.()) : setOuvertLocal(v));
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  const envoye = devis.status !== 'draft';
  // ── UN BROUILLON SE SUPPRIME EN DEUX CLICS — 6 octobre 2026 ──
  //
  // Philippe : recopier la référence pour jeter un essai, c'était trop
  // lourd. La recopie reste pour un devis qui n'est plus un brouillon :
  // celui-là a été remis à un client.
  const correspond = !envoye || saisie.trim() === devis.reference;

  function fermer() {
    setOuvert(false);
    setSaisie('');
    setErreur(null);
  }

  async function supprimer() {
    if (!correspond) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await api.delete(`/devis/${devis.id}`);
      // Le parent navigue : rester sur l'URL d'un devis qui n'existe
      // plus afficherait une erreur de chargement à la place d'un
      // succès, et ferait croire que la suppression a échoué.
      await onSupprime?.();
    } catch (err) {
      setErreur(err);
      setEnvoi(false);
    }
  }

  return (
    <>
      {sansBouton ? null : (
        <Button size="sm" variant="ghost" onClick={() => setOuvert(true)}>
          Supprimer le devis
        </Button>
      )}

      <Modal
        open={ouvert}
        onClose={fermer}
        title={`Supprimer le devis ${devis.reference} ?`}
        footer={
          <>
            <Button variant="secondary" onClick={fermer} disabled={envoi}>
              Annuler
            </Button>
            <Button variant="danger" onClick={supprimer} disabled={!correspond || envoi}>
              {envoi ? 'Suppression…' : 'Supprimer'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>
            Cette suppression est <strong>définitive</strong>. Elle emporte{' '}
            {pieces > 0 ? <strong>{pieces} pièce(s)</strong> : 'les pièces'}, leur gamme
            opératoire, les paliers de quantité, la sous-traitance, les documents générés —{' '}
            <strong>et les plans du client sont effacés du disque</strong>. Seule la sauvegarde de
            la nuit permettrait de revenir en arrière.
          </p>

          {envoye ? (
            <p className="warnings" role="alert">
              Ce devis n’est pas un brouillon : il est <strong>{devis.status}</strong>. C’est une
              pièce commerciale, et elle a peut-être été remise à un client.
            </p>
          ) : null}

          {envoye ? (
            <TextField
              label={`Recopiez la référence pour confirmer : ${devis.reference}`}
              value={saisie}
              autoComplete="off"
              onChange={(e) => setSaisie(e.target.value)}
            />
          ) : null}

          {erreur ? (
            <p className="blocage__erreur" role="alert">
              {erreur.message}
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

export default SupprimerDevis;
